// PWA-wide date formatting. The admin chooses a preset under Settings;
// every visible date in the PWA flows through `formatDate` so they all
// follow that choice. Falls back to ISO if no preset is set yet.
//
// Module-level state + pub/sub. App boots → fetches the global setting
// → calls setDateFormat(preset). Components that want to re-render
// when the format changes call useDateFormatVersion() (dateFormat.js).
//
// This file is dependency-free (no React) so the node suite can load it —
// dateFormat.js re-exports it and adds the hook.

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const pad = (n) => String(n).padStart(2, '0');

const PRESETS = {
  iso: {
    date: (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    datetime: (d, formatDate) => `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
  },
  eu: {
    date: (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
    datetime: (d, formatDate) => `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
  },
  us: {
    date: (d) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`,
    datetime: (d, formatDate) => {
      const h12 = d.getHours() % 12 || 12;
      const ampm = d.getHours() < 12 ? 'AM' : 'PM';
      return `${formatDate(d)} ${h12}:${pad(d.getMinutes())} ${ampm}`;
    },
  },
  long: {
    date: (d) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`,
    datetime: (d, formatDate) => `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
  },
  short: {
    date: (d) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`,
    datetime: (d, formatDate) => `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
  },
};

export const DATE_FORMAT_PRESETS = [
  { key: 'iso',   label: 'ISO',           sample: '2026-05-11 (14:30)' },
  { key: 'eu',    label: 'European',      sample: '11/05/2026 (14:30)' },
  { key: 'us',    label: 'United States', sample: '05/11/2026 (2:30 PM)' },
  { key: 'long',  label: 'Long',          sample: '11 May 2026 (14:30)' },
  { key: 'short', label: 'Short (no year)', sample: '11 May (14:30)' },
];

let currentPreset = 'iso';
const listeners = new Set();
let version = 0;

export function getDateFormatVersion() { return version; }
export function subscribeDateFormat(fn) { listeners.add(fn); return () => listeners.delete(fn); }

export function setDateFormat(preset) {
  if (!preset || !PRESETS[preset]) preset = 'iso';
  if (preset === currentPreset) return;
  currentPreset = preset;
  version++;
  for (const fn of listeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function getDateFormat() {
  return currentPreset;
}

// Coerce arbitrary inputs (Date, ISO string, ms epoch) into a Date.
// Returns null if the value isn't a parseable date.
function toDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

// `kind` is 'date' (default) or 'datetime'.
// Pass a fallback string for non-parseable values; default is ''.
export function formatDate(value, kind = 'date', fallback = '') {
  const d = toDate(value);
  if (!d) return fallback;
  const preset = PRESETS[currentPreset] || PRESETS.iso;
  if (kind === 'datetime') {
    const dateOnly = (innerD) => preset.date(innerD);
    return preset.datetime(d, dateOnly);
  }
  return preset.date(d);
}

// A formula's date result reaches the grid as TEXT in the engine's canonical
// form — "2026-09-17", or "2026-09-17 14:30" when it carries a time (expr
// builtins formatDateValue; the Formula Store keeps the same text). That
// text is machine-stable on purpose; it is not the admin's chosen face. The
// grids run every formula cell through this so a computed date reads like a
// date field beside it (On-Track's default view showed ISO dates in formula
// columns under a US preset, 2026-09-22). Anything else comes back null.
// Date-only text is built as a LOCAL date so the calendar day never shifts
// with the viewer's timezone.
const CANON_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CANON_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;
export function formatIfCanonicalDate(text) {
  if (typeof text !== 'string') return null;
  let m = CANON_DATETIME_RE.exec(text);
  if (m) return formatDate(new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]), 'datetime', text);
  m = CANON_DATE_RE.exec(text);
  if (m) return formatDate(new Date(+m[1], +m[2] - 1, +m[3]), 'date', text);
  return null;
}

